import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { Alert, Spinner } from "react-bootstrap";
import { IconAlertTriangle, IconRobot } from "@tabler/icons-react";
import PhoneValidation from "./PhoneValidation";
import PublicChat from "./PublicChat";
import usePublicAgent from "../../../hooks/usePublicAgent";
import Swal from "sweetalert2";
import "./public-playground.css";

const VALIDATION_STATES = {
  TURNSTILE_VALIDATION: "TURNSTILE_VALIDATION",
  LOADING_AGENT: "LOADING_AGENT",
  CHAT_ACTIVE: "CHAT_ACTIVE",
  ERROR: "ERROR",
};

const PublicPlayground = () => {
  const { agentName, token } = useParams();
  const {
    isLoading,
    error,
    sessionToken,
    isValidated,
    validatePhoneAccess,
    getAgent,
    clearError,
    clearSession,
  } = usePublicAgent();

  const [currentState, setCurrentState] = useState(
    VALIDATION_STATES.TURNSTILE_VALIDATION
  );
  const [agent, setAgent] = useState(null);
  const [validationError, setValidationError] = useState("");
  const [agentError, setAgentError] = useState("");

  // Ref para controlar o reload do Turnstile
  const turnstileResetTrigger = useRef(0);

  // Limpar estados ao montar componente
  useEffect(() => {
    clearSession();
    clearError();
    setCurrentState(VALIDATION_STATES.TURNSTILE_VALIDATION);
    setAgent(null);
    setValidationError("");
    setAgentError("");
  }, [agentName, token, clearSession, clearError]);

  // Validar parâmetros da URL
  useEffect(() => {
    if (!agentName || !token) {
      setCurrentState(VALIDATION_STATES.ERROR);
      setAgentError("URL inválida. Parâmetros obrigatórios não encontrados.");
      return;
    }

    if (token.length < 16) {
      setCurrentState(VALIDATION_STATES.ERROR);
      setAgentError("Token de acesso inválido.");
      return;
    }
  }, [agentName, token]);

  // Função para tratar expiração do Turnstile
  const handleTurnstileExpiry = async () => {
    const result = await Swal.fire({
      icon: "warning",
      title: "Validação Anti-Bot Expirou",
      html: `
        <div style="text-align: left; padding: 10px;">
          <p><strong>A verificação de segurança expirou!</strong></p>
          <hr>
          <p class="text-info">
            <i class="las la-shield-alt"></i>
            Por motivos de segurança, a validação anti-bot tem tempo limitado.
          </p>
          <p class="text-muted">
            <i class="las la-refresh"></i>
            Clique em "Tentar Novamente" para realizar uma nova verificação.
          </p>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: "Tentar Novamente",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#3b82f6",
      cancelButtonColor: "#6c757d",
      width: 500,
      customClass: {
        container: "turnstile-expiry-modal",
      },
    });

    if (result.isConfirmed) {
      window.location.reload()

      // Mostrar mensagem de sucesso
      Swal.fire({
        icon: "success",
        title: "Verificação Resetada",
        text: "Complete a nova verificação de segurança abaixo.",
        timer: 2000,
        showConfirmButton: false,
        toast: true,
        position: "top-end",
        timerProgressBar: true,
      });
    }
  };

  const handleValidationSuccess = async (turnstileToken) => {
    setValidationError("");

    try {
      const result = await validatePhoneAccess(
        token,
        "",
        turnstileToken
      );

      if (result.success) {
        setCurrentState(VALIDATION_STATES.LOADING_AGENT);
        await loadAgent();
      } else {
        // Verificar se é erro de expiração/falha do Turnstile
        const isTurnstileError =
          result.errorCode === "TURNSTILE_FAILED" ||
          result.error?.includes("Validação de segurança falhou") ||
          result.error?.includes("Falha na verificação de segurança");

        // Debug para verificar detecção
        console.log("=== VALIDATION ERROR DEBUG ===");
        console.log("result.error:", result.error);
        console.log("result.errorCode:", result.errorCode);
        console.log("isTurnstileError:", isTurnstileError);

        if (isTurnstileError) {
          await handleTurnstileExpiry();
        } else {
          setValidationError(result.error || "Erro na validação do acesso");
        }
      }
    } catch (error) {
      console.error("Erro na validação:", error);
      setValidationError("Erro inesperado na validação. Tente novamente.");
    }
  };

  const loadAgent = async () => {
    try {
      const result = await getAgent(token);

      if (result.success) {
        console.log(result);
        // Verificar se o agente está ativo e válido
        if (!result.isValid || !result.agent.is_available) {
          setCurrentState(VALIDATION_STATES.ERROR);
          setAgentError("Este agente não está disponível para teste público.");
          return;
        }

        setAgent(result.agent);
        setCurrentState(VALIDATION_STATES.CHAT_ACTIVE);
      } else {
        setCurrentState(VALIDATION_STATES.ERROR);
        setAgentError(result.error || "Agente não encontrado ou indisponível.");
      }
    } catch (error) {
      console.error("Erro ao carregar agente:", error);
      setCurrentState(VALIDATION_STATES.ERROR);
      setAgentError("Erro ao carregar informações do agente.");
    }
  };

  const handleBackToValidation = () => {
    clearSession();
    setCurrentState(VALIDATION_STATES.TURNSTILE_VALIDATION);
    setAgent(null);
    setValidationError("");
    setAgentError("");
  };

  const renderContent = () => {
    switch (currentState) {
      case VALIDATION_STATES.TURNSTILE_VALIDATION:
        return (
          <PhoneValidation
            agentName={agentName}
            onValidationSuccess={handleValidationSuccess}
            isLoading={isLoading}
            error={validationError || error}
            turnstileResetTrigger={turnstileResetTrigger.current}
          />
        );

      case VALIDATION_STATES.LOADING_AGENT:
        return (
          <div className="public-loading-container">
            <div className="public-loading-content">
              <div
                className="mx-auto mb-3 rounded-circle d-flex align-items-center justify-content-center"
                style={{
                  width: "64px",
                  height: "64px",
                  background: "var(--gradient-primary)",
                  color: "white",
                }}
              >
                <IconRobot size={32} />
              </div>

              <h4 className="fw-bold text-primary mb-3">
                Carregando {agentName}...
              </h4>

              <Spinner
                animation="border"
                variant="primary"
                size="sm"
                className="mb-3"
              />

              <p className="text-muted mb-0">
                Preparando o agente para conversa
              </p>
            </div>
          </div>
        );

      case VALIDATION_STATES.CHAT_ACTIVE:
        return (
          <PublicChat
            agent={agent}
            sessionToken={sessionToken}
            agentToken={token}
            onBack={handleBackToValidation}
          />
        );

      case VALIDATION_STATES.ERROR:
        return (
          <div className="public-error-container">
            <div className="public-error-content">
              <div
                className="mx-auto mb-3 rounded-circle d-flex align-items-center justify-content-center"
                style={{
                  width: "64px",
                  height: "64px",
                  background: "#dc3545",
                  color: "white",
                }}
              >
                <IconAlertTriangle size={32} />
              </div>

              <h4 className="fw-bold text-danger mb-3">Erro de Acesso</h4>

              <Alert variant="danger" className="text-start">
                {agentError}
              </Alert>

              <div className="text-center mt-4">
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={() => window.location.reload()}
                  style={{
                    borderRadius: "12px",
                    padding: "8px 20px",
                    fontWeight: "600",
                  }}
                >
                  Recarregar Página
                </button>
              </div>

              <div className="text-center mt-3">
                <small className="text-muted">
                  Verifique se a URL está correta ou entre em contato com o
                  responsável
                </small>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="public-playground-wrapper">
      {/* Conteúdo principal */}
      <div className="public-playground-content">{renderContent()}</div>
    </div>
  );
};

export default PublicPlayground;
